import { Translation } from '@ps/web-shared-components';
import React, { useCallback, useEffect, useState } from 'react';
import type { PressEvent } from 'react-aria';

import useRequest from '@swf/core/hooks/useRequest';
import type { ArticlesList, Category } from '@swf/help';
import { ArticleList } from '@swf/help';

import { getSideNavs } from '../../utils';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import LoadMore from '../LoadMore/LoadMore';
import SideBar from '../SideBar/SideBar';

import {
  TXTWCL_All_help_categories,
  TXTWCL_Search,
  TXTWCL_Search_results_for_XN0X,
  TXTWCL_SorryX_we_couldnXt_find_anythi_ELL
} from './SearchList.messages.json';
import styles from './SearchList.scss';

interface Props {
  categories: Category[];
  searchList: ArticlesList;
  pageSize: string;
}

const SearchList: React.FC<Props> = ({ categories, searchList = {}, pageSize }) => {
  const { articles: articlesList = [], meta } = searchList;
  const [articles, setArticles] = useState(articlesList);
  const count = meta?.count || 0;
  const term = meta?.search_term || '';

  const sideNavs = getSideNavs(categories);
  const request = useRequest();

  useEffect(() => setArticles(articlesList), [articlesList]);

  const loadMore = useCallback(
    (_e: PressEvent) => {
      const pagesCount = Math.ceil(articles.length / parseInt(pageSize, 10)) + 1;

      request({
        service: `help.searchArticlesByTerm`,
        body: { searchTerm: encodeURIComponent(term), page: pagesCount, size: pageSize }
      }).subscribe((result) => {
        const { articles: newArticles } = result.body;

        setArticles([...articles, ...newArticles]);
      });
    },
    [articles, pageSize, request, term]
  );

  return (
    <section>
      <div className={styles.inner}>
        <Breadcrumb id="search-id" title={<Translation {...TXTWCL_Search} />} />
        <h1 className={styles.title}>
          <Translation values={{ 0: term }} {...TXTWCL_Search_results_for_XN0X} />
        </h1>
        <div className={styles.contentWrapper}>
          <div className={styles.content}>
            {count > 0 ? (
              <>
                <ArticleList articles={articles} regularHeight />
                <LoadMore len={articles.length} count={count} loadMore={loadMore} />
              </>
            ) : (
              <p className={styles.noResult}>
                <Translation values={{ 0: term }} {...TXTWCL_SorryX_we_couldnXt_find_anythi_ELL} />
              </p>
            )}
          </div>
          <div className={styles.sideBar}>
            <SideBar sideNavs={sideNavs} title={<Translation {...TXTWCL_All_help_categories} />} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SearchList;
